import { Controller, useFormContext } from 'react-hook-form'

import { PureSupplierRelationshipsSelectionDetailedSupplierFragment } from '@/graphql/purchase-plus/generated/purchasePlus_graphql'
import { PureSupplierRelationshipsSelectionDetailed } from '@/modules/shared/components/pure-comboboxes/PureSupplierRelationshipsSelectionDetailed'
import { Tooltip } from '@/modules/shared/components/tooltip/Tooltip'
import { QuestionMarkIcon } from '@/modules/shared/icons/QuestionMarkIcon'

interface FormSelectSupplierRelationshipProps {
  title: string
  description?: string | React.JSX.Element
  register: string
  placeholder: string
  required?: boolean
  requiredError?: string
  onSelected?: (value: PureSupplierRelationshipsSelectionDetailedSupplierFragment) => void
  className?: string
  tooltip?: string
  showChips?: boolean
  defaultValue?: PureSupplierRelationshipsSelectionDetailedSupplierFragment
  disabled?: boolean
  apSystemId?: number
  purchaserId?: number
  saveAsObject?: boolean
  /** When true, supplier list renders in a portal (use inside scroll/overflow containers). */
  floating?: boolean
}

export default function FormSelectSupplierRelationship({
  title,
  description,
  register,
  placeholder,
  required,
  requiredError,
  onSelected,
  className,
  tooltip,
  showChips = true,
  defaultValue,
  disabled,
  apSystemId,
  purchaserId,
  saveAsObject = false,
  floating,
}: FormSelectSupplierRelationshipProps) {
  const {
    control,
    resetField,
    formState: { errors },
    getValues,
  } = useFormContext()

  return (
    <section className={className || 'mt-8'}>
      <h2 className="flex items-center gap-x-1.5 text-sm font-semibold">
        {title}
        {tooltip && (
          <Tooltip content={tooltip}>
            <QuestionMarkIcon className="size-4" />
          </Tooltip>
        )}
      </h2>
      <p className="text-sm text-gray-500">{description}</p>
      <div className="mt-2">
        <Controller
          control={control}
          name={register}
          rules={{ required }}
          render={({ field }) => (
            <PureSupplierRelationshipsSelectionDetailed
              placeholder={placeholder}
              onSelected={(e) => {
                if (e) {
                  onSelected?.(e)
                  const current = getValues(register) as { id?: number } | number | null | undefined
                  const currentId = typeof current === 'object' ? current?.id : current
                  if (currentId === e.id) return

                  if (saveAsObject) {
                    field.onChange({
                      id: e.id,
                      name: e.name,
                      businessNumber: e.businessNumber,
                      isVerified: e.isVerified,
                      owner: e.owner,
                      parent: e.parent,
                      legalEntity: e.legalEntity,
                      paperlessMatchString: e.paperlessMatchString,
                    })
                  } else {
                    field.onChange(e.id)
                  }
                }
              }}
              hasError={Boolean(errors[register])}
              errorMessage={requiredError}
              onChipRemove={() => resetField(register)}
              showChips={showChips}
              defaultValue={defaultValue}
              disabled={disabled}
              apSystemId={apSystemId}
              purchaserId={purchaserId}
              floating={floating}
            />
          )}
        />
      </div>
    </section>
  )
}
